<template>
  <teleport to="body">
    <div
      id="modalRoot"
      ref="modalRoot"
      :tabindex="-1"
      role="dialog"
      aria-modal="true"
      :aria-label="title || undefined"
      class="modal-backdrop absolute right-0 left-0 top-0 bottom-0 z-50 flex items-start lg:items-center justify-center bg-neutral-900/20"
    >
      <div
        ref="modalBody"
        class="modal-panel flex min-h-full min-w-full flex-col gap-6 rounded-lg bg-white p-6 lg:min-h-[175px] lg:min-w-[515px]"
        :class="optionalBodyClasses"
      >
        <div class="flex flex-col">
          <div class="flex items-center justify-between">
            <h2
              class="text-2xl font-bold leading-7 tracking-tight text-neutral-900"
              :class="{
                'font-bold': titleWeight === 'bold',
                'font-normal': titleWeight === 'normal'
              }"
            >
              {{ title }}
            </h2>
            <button
              v-if="!disableClose"
              data-testid="modal-close-button"
              type="button"
              :aria-label="t('action.close')"
              class="-m-2 p-2 text-neutral-800 transition-colors duration-150 ease-out-quart hover:text-neutral-500"
              @click="close"
            >
              <font-awesome-icon :icon="['fas', 'xmark']" size="1x" class="w-4" />
            </button>
          </div>

          <div class="flex flex-col">
            <h3 v-if="description" class="text-xs font-normal text-neutral-600">{{ description }}</h3>
            <slot name="header" />
          </div>
        </div>

        <div class="flex-1">
          <slot />
        </div>

        <div v-if="$slots.actions" class="flex items-center justify-end gap-2">
          <slot name="actions" :close="close" />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { t } = useI18n();

const emit = defineEmits(['close']);
const modalBody = ref(null);

interface Props {
  title?: string;
  description?: string;
  clickOutside?: boolean;
  disableClose?: boolean;
  titleWeight?: 'bold' | 'normal';
  optionalBodyClasses?: string;
}

const props = withDefaults(defineProps<Props>(), {
  titleWeight: 'bold',
  title: '',
  description: '',
  optionalBodyClasses: ''
});

if (props.clickOutside) {
  onClickOutside(modalBody, (event) => close());
}

// Document-level so Escape works no matter where focus is, but only the
// top-most (last-mounted) modal responds when modals are stacked.
onKeyStroke('Escape', () => {
  const roots = document.querySelectorAll('.modal-backdrop');

  if (roots.length === 0 || roots[roots.length - 1] === modalRoot.value) {
    close();
  }
});

function close(): void {
  if (props.disableClose) {
    return;
  }

  emit('close');
}

// Move focus into the dialog on open so keyboard and screen-reader users
// land inside it.
const modalRoot = ref();
onMounted(() => {
  modalRoot.value?.focus();
});
</script>

<style scoped>
/* Mount-only entrance: parents toggle the modal with v-if, so a CSS
   animation is the only thing that reliably plays on open. */
.modal-backdrop {
  animation: modal-backdrop-in 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

.modal-panel {
  animation: modal-panel-in 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

@keyframes modal-backdrop-in {
  from {
    opacity: 0;
  }
}

@keyframes modal-panel-in {
  from {
    transform: scale(0.96);
  }
}

@media (prefers-reduced-motion: reduce) {
  .modal-panel {
    animation: none;
  }
}
</style>
